import React, { useState, useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { 
  MessageCircle, 
  Send, 
  X, 
  Bot,
  Loader2,
  Sparkles,
  ChevronDown,
  HelpCircle,
  Stethoscope,
  Calendar,
  Pill,
  Activity,
  Shield,
  Heart,
  Baby,
  Users,
  AlertTriangle,
  Map,
  Dna,
  Trophy,
  Watch
} from 'lucide-react';
import axios from 'axios';
import { API } from '@/App';
import { useNavigate } from 'react-router-dom';

// Feature quick actions for the chatbot
const QUICK_ACTIONS = [
  { icon: Stethoscope, label: 'Check Symptoms', path: '/symptom-checker', desc: 'AI-powered symptom analysis' },
  { icon: Calendar, label: 'Appointments', path: '/appointments', desc: 'Schedule & manage appointments' },
  { icon: Pill, label: 'Medicine Scanner', path: '/medicine-scanner', desc: 'Scan & identify medicines' },
  { icon: Activity, label: 'Health Data', path: '/health-data', desc: 'Track your vitals' },
  { icon: Shield, label: 'Emergency SOS', path: '/emergency-sos', desc: 'Emergency contacts & alerts' },
  { icon: Baby, label: 'Vaccination', path: '/vaccination-tracker', desc: 'Child vaccination tracker' },
  { icon: Users, label: 'Family Health', path: '/family-dashboard', desc: 'Manage family health' },
  { icon: Dna, label: 'Genetic Health', path: '/genetic-health-map', desc: 'Family health history' },
  { icon: Trophy, label: 'HealthBet', path: '/healthbet', desc: 'Health challenges & rewards' },
  { icon: Watch, label: 'Wearables', path: '/terra-wearables', desc: 'Connect smartwatch' },
];

// System prompt for the help assistant
const HELP_SYSTEM_PROMPT = `You are HealthAI Assistant, a helpful guide for the HealthAI healthcare platform. Your role is to:

1. Help users navigate the app and find features
2. Explain how different features work
3. Answer questions about health tracking, appointments, medications, etc.
4. Guide users to the right section of the app

Available features in HealthAI:
- Symptom Checker: AI-powered symptom analysis
- Appointments: Schedule video consultations with doctors
- Medicine Scanner: Scan pills to identify and check interactions
- Health Data: Track vitals like heart rate, blood pressure, weight
- Emergency SOS: Set up emergency contacts and trigger SOS alerts
- Vaccination Tracker: Track child vaccinations
- Family Dashboard: Manage health for family members
- Elderly Care: Monitor and care for elderly family members
- Pregnancy Tracker: Track pregnancy journey with AI insights
- Genetic Health Map: Visualize family health history
- HealthBet: Join health challenges and win rewards
- Wearables: Connect Fitbit, Apple Watch, Garmin, etc.
- Insurance Hub: Manage insurance policies and claims
- First Aid Guide: Emergency first aid instructions
- Nearby Clinics: Find hospitals and pharmacies
- AI Health Predictions: 6-month health risk forecasting

Be friendly, concise, and helpful. If a user asks about a feature, briefly explain it and suggest they try it.
For medical questions, remind them this is for guidance only and to consult a healthcare professional.`;

const HelpChatbot = ({ user }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: `Hi ${user?.full_name?.split(' ')[0] || 'there'}! 👋 I'm your HealthAI Assistant. How can I help you today?\n\nYou can ask me about any feature or tap a quick action below!`
    }
  ]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showQuickActions, setShowQuickActions] = useState(true);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const handleSendMessage = async (messageText = inputValue) => {
    if (!messageText.trim() || isLoading) return;

    const userMessage = { role: 'user', content: messageText.trim() };
    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setShowQuickActions(false);
    setIsLoading(true);

    try {
      const response = await axios.post(`${API}/chat/help-assistant`, {
        message: messageText.trim(),
        context: {
          user_name: user?.full_name,
          current_page: 'dashboard'
        }
      });

      const assistantMessage = {
        role: 'assistant',
        content: response.data.response,
        suggested_action: response.data.suggested_action
      };
      setMessages(prev => [...prev, assistantMessage]);
    } catch (error) {
      console.error('Error sending message:', error);
      // Fallback response if API fails
      const fallbackResponse = getFallbackResponse(messageText);
      setMessages(prev => [...prev, { role: 'assistant', content: fallbackResponse }]);
    } finally {
      setIsLoading(false);
    }
  };

  const getFallbackResponse = (query) => {
    const lowerQuery = query.toLowerCase();
    
    if (lowerQuery.includes('symptom') || lowerQuery.includes('sick') || lowerQuery.includes('feeling')) {
      return "For symptom analysis, go to **Symptom Checker** in the menu. Our AI will analyze your symptoms and provide guidance. Remember to consult a doctor for proper diagnosis!";
    }
    if (lowerQuery.includes('appointment') || lowerQuery.includes('doctor') || lowerQuery.includes('consult')) {
      return "You can schedule appointments from the **Appointments** section. We offer video consultations with qualified doctors!";
    }
    if (lowerQuery.includes('medicine') || lowerQuery.includes('pill') || lowerQuery.includes('drug')) {
      return "Use the **Medicine Scanner** to identify pills and check for drug interactions. Just take a photo of your medication!";
    }
    if (lowerQuery.includes('emergency') || lowerQuery.includes('sos') || lowerQuery.includes('urgent')) {
      return "For emergencies, go to **Emergency SOS**. You can set up emergency contacts and trigger SOS alerts that share your location!";
    }
    if (lowerQuery.includes('family') || lowerQuery.includes('child') || lowerQuery.includes('kids')) {
      return "Check out **Family Dashboard** to manage health for your entire family, or **Vaccination Tracker** for children's immunizations!";
    }
    if (lowerQuery.includes('wearable') || lowerQuery.includes('watch') || lowerQuery.includes('fitbit')) {
      return "Connect your smartwatch in **Wearables**! We support Fitbit, Apple Watch, Garmin, and 100+ other devices.";
    }
    if (lowerQuery.includes('insurance') || lowerQuery.includes('claim') || lowerQuery.includes('policy')) {
      return "Manage your insurance in **Insurance Hub**. You can store policies, track claims, and find cashless hospitals!";
    }
    
    return "I can help you navigate HealthAI! Try asking about specific features like symptom checker, appointments, medicine scanner, or emergency SOS. You can also tap the quick actions below!";
  };

  const handleQuickAction = (action) => {
    navigate(action.path);
    setIsOpen(false);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <>
      {/* Floating Button */}
      <div className="fixed bottom-6 right-6 z-[9999]" style={{ position: 'fixed' }}>
        {!isOpen && (
          <Button
            onClick={() => setIsOpen(true)}
            className="w-16 h-16 rounded-full bg-gradient-to-r from-teal-500 to-cyan-600 hover:from-teal-600 hover:to-cyan-700 shadow-2xl hover:shadow-xl transition-all duration-300 group animate-pulse hover:animate-none"
            data-testid="help-chatbot-btn"
          >
            <MessageCircle className="w-7 h-7 text-white group-hover:scale-110 transition-transform" />
          </Button>
        )}

        {/* Chat Window */}
        {isOpen && (
          <Card className="w-[380px] h-[550px] flex flex-col shadow-2xl border-0 overflow-hidden animate-in slide-in-from-bottom-5 duration-300">
            {/* Header */}
            <div className="bg-gradient-to-r from-teal-500 to-cyan-600 p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                  <Bot className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-white">HealthAI Assistant</h3>
                  <p className="text-xs text-white/80">Always here to help</p>
                </div>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setIsOpen(false)}
                className="text-white hover:bg-white/20"
              >
                <X className="w-5 h-5" />
              </Button>
            </div>

            {/* Messages */}
            <ScrollArea className="flex-1 p-4 bg-gray-50">
              <div className="space-y-4">
                {messages.map((message, index) => (
                  <div
                    key={`item-${index}`}
                    className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-[85%] rounded-2xl px-4 py-2.5 ${
                        message.role === 'user'
                          ? 'bg-gradient-to-r from-teal-500 to-cyan-600 text-white rounded-br-md'
                          : 'bg-white shadow-sm border border-gray-100 text-gray-800 rounded-bl-md'
                      }`}
                    >
                      <p className="text-sm whitespace-pre-wrap leading-relaxed">
                        {message.content.split('**').map((part, i) => 
                          i % 2 === 1 ? <strong key={`item-${i}`}>{part}</strong> : part
                        )}
                      </p>
                      {message.suggested_action && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="mt-2 text-xs"
                          onClick={() => navigate(message.suggested_action.path)}
                        >
                          {message.suggested_action.label} →
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
                
                {isLoading && (
                  <div className="flex justify-start">
                    <div className="bg-white shadow-sm border border-gray-100 rounded-2xl rounded-bl-md px-4 py-3">
                      <div className="flex items-center gap-2">
                        <Loader2 className="w-4 h-4 animate-spin text-teal-600" />
                        <span className="text-sm text-gray-500">Thinking...</span>
                      </div>
                    </div>
                  </div>
                )}
                
                <div ref={messagesEndRef} />
              </div>
            </ScrollArea>

            {/* Quick Actions */}
            {showQuickActions && (
              <div className="px-4 py-3 bg-white border-t border-gray-100">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-medium text-gray-500">Quick Actions</span>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 text-xs text-gray-400"
                    onClick={() => setShowQuickActions(false)}
                  >
                    <ChevronDown className="w-3 h-3" />
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {QUICK_ACTIONS.slice(0, 6).map((action, index) => (
                    <Button
                      key={`item-${index}`}
                      variant="outline"
                      size="sm"
                      className="text-xs h-8 gap-1.5 hover:bg-teal-50 hover:text-teal-700 hover:border-teal-300"
                      onClick={() => handleQuickAction(action)}
                    >
                      <action.icon className="w-3.5 h-3.5" />
                      {action.label}
                    </Button>
                  ))}
                </div>
              </div>
            )}

            {/* Input */}
            <div className="p-4 bg-white border-t border-gray-100">
              <div className="flex gap-2">
                <Input
                  ref={inputRef}
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Ask me anything..."
                  className="flex-1 border-gray-200 focus:border-teal-400 focus:ring-teal-400"
                  disabled={isLoading}
                  data-testid="help-chatbot-input"
                />
                <Button
                  onClick={() => handleSendMessage()}
                  disabled={!inputValue.trim() || isLoading}
                  className="bg-gradient-to-r from-teal-500 to-cyan-600 hover:from-teal-600 hover:to-cyan-700"
                  data-testid="help-chatbot-send"
                >
                  <Send className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </Card>
        )}
      </div>
    </>
  );
};

export default HelpChatbot;
